import {
    faCheckCircle,
    faToolbox,
    faTruck,
    faCommentDots,
    faComments,
    faTimesCircle,
    faPhoneSquareAlt,
    faUser,
    faCircleNotch,
    faShuttleVan,
    faHardHat,
    faUserClock,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { withRouter } from 'react-router-dom';
import { Alert, Button, Container, FormGroup } from 'reactstrap';
import './AssignmentConfirmation.scss';
import cls from 'classnames';
import _ from 'lodash-es';
import { CircularProgress } from '@mui/material';
import moment from 'moment';
import { util } from '../Util';
import { fromUtcToLocalMoment } from '../utilities/DateHelpers';
import {
    CollapseUnderlineHeader,
    FlexBetweenRow,
    FlexCenterRow,
    FlexColumnStart,
    FormLabel,
    toasty,
} from '../common/forms/FormElements';
import CommonContext, { LocalizationKeys as l } from '../Common';
import { ApiRoutes } from '../ApiRoutes';
import { AppNavPaths } from '../AppNavPaths';
import {
    ContractTimesheetResponsibilityType,
    TimesheetResponsibilityType,
} from '../tenant/Tenant';
import authService from '../api-authorization/AuthorizeService';
import { JobDetails } from '../job/JobDetails';
import { AssignmentLocationDisplay } from './AssignmentLocationDisplay';
import JobResourceOverrideDetails from '../dispatch/JobResourceOverrideDetails';

export const ConfirmationStatus = {
    Unfilled: 1,
    Scheduled: 2,
    NoResponse: 3,
    Confirmed: 4,
    CancelPending: 5,
    ThresholdCancel: 6,
};

export const JobConfirmationStatusOptions = [
    { value: 1, class: 'unfilled', label: 'Unfilled' },
    { value: 2, class: 'scheduled', label: 'Scheduled' },
    { value: 3, class: 'noresponse', label: 'No Response' },
    { value: 4, class: 'confirmed', label: 'Confirmed' },
    { value: 5, class: 'cancelpending', label: 'Cancel Pending' },
];

class AssignmentConfirmation extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.state = {
            assignment: null,
            confirmedAssignments: [],
            crewAndEquipOpen: true,
            invalidChargeTypeIds: [],
            jobDetailsOpen: false,
            loading: true,
            saving: false,
            notFound: false,
            timesheetOpen: true,
            timesheetValid: false,
            timesheetValidated: false,
            enforceCrewLeadTimesheet: true,
        };
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    onAddChargeTypeToTimesheet = async () => {};

    onAssignmentCheckedChanged = (id) => {
        const { confirmedAssignments } = { ...this.state };
        const inx = confirmedAssignments.indexOf(parseInt(id));
        if (inx < 0) confirmedAssignments.push(parseInt(id));
        else confirmedAssignments.splice(inx, 1);
        this.setState({ confirmedAssignments });
    };

    confirmAssignment = async () => {
        const { assignment, confirmedAssignments } = this.state;

        // What equipment does the confirming employee own of this list?
        const ownedEquipment = assignment.equipment.filter(
            (x) =>
                x.equipmentEmployeeAssignmentId ===
                assignment.assignedEmployeeId
        );
        const ownedEquipmentIds = ownedEquipment.map(
            (x) => x.assignedEquipmentId
        );

        // Of the equipment, which have been assigned to any of the equipment owned by the confirming employee?
        const dependentEquipment = assignment.equipment.filter((x) =>
            ownedEquipmentIds.includes(x.equipmentAssignedEquipmentId)
        );
        // const dependentEquipmentIds = dependentEquipment.map(x => x.assignedEquipmentId);

        // Get all assignments this emp needs to acknowledge
        const equipmentAssignmentsToConfirm = new Set([
            ...ownedEquipment.map((e) => e.id),
            ...dependentEquipment.map((e) => e.id),
        ]);

        const toConfirm = _.sortBy([...equipmentAssignmentsToConfirm]);
        const allConfirmed = _.sortBy(confirmedAssignments);
        const valid = _.isEqual(_.sortBy(toConfirm), _.sortBy(allConfirmed));

        if (!valid) {
            toasty.error('Please confirm all equipment assignments.');
        } else {
            this.setState({ saving: true });
            const timesheetId = await util.fetch.post(
                ApiRoutes.jobAssignment.confirm(assignment.jobAssignmentId),
                { notes: null }
            );

            if (timesheetId) {
                toasty.success('Assignment confirmed.');
                window.location.reload();
            } else {
                toasty.error('Error confirming assignment.');
            }

            this.setState({ saving: false });
        }
    };

    confirmCancelledAssignment = async () => {
        const { assignment } = { ...this.state };
        this.setState({ saving: true });
        const timesheetId = await util.fetch.put(
            ApiRoutes.jobAssignment.cancelConfirmed(assignment.jobAssignmentId)
        );

        this.setState({ saving: false });

        if (timesheetId !== 0)
            this.props.history.push(`${AppNavPaths.Timesheet}/${timesheetId}`);
        else this.props.history.push(`${AppNavPaths.AssignmentCalendar}`);
    };

    populateState = async () => {
        const isAuthenticated = await authService.isAuthenticated();
        if (isAuthenticated) {
            const assignmentId = this.props.match.params.id;

            const strings = await util.l10n.getStrings([l.crewLead]);
            this.setState({ strings });

            if (!assignmentId) {
                this.setState({ notFound: true, loading: false });
            } else {
                const response = await util.fetch.get(
                    ApiRoutes.jobAssignment.byId(assignmentId),
                    util.fetch.format.none
                );
                if (response.ok) {
                    const assignment = await response.json();

                    if (!assignment) {
                        this.setState({ notFound: true, loading: false });
                    } else {
                        /**
                         *  Determine who fills out the timesheets.  THis will be used to determine where to direct the user after
                         *  assignment confirmation.
                         * */
                        const enforceCrewLeadTS =
                            // Are we set for crew lead timesheets at the tenant level?
                            this.context.tenant.tenantSettings
                                .timesheetResponsibilityType ===
                                TimesheetResponsibilityType.CrewLead ||
                            // Are we set for timesheets per contract, and contract is set up for crew lead?
                            (this.context.tenant.tenantSettings
                                .timesheetResponsibilityType ===
                                TimesheetResponsibilityType.Contract &&
                                assignment.contractTimesheetResponsibilityTypeId ===
                                    ContractTimesheetResponsibilityType.CrewLead);

                        const arcGisLocation = await util.fetch.js(
                            ApiRoutes.arcGisLocations.forAssignment(
                                assignmentId
                            )
                        );

                        const confirmed =
                            (assignment ?? {}).confirmationStatusId ===
                            ConfirmationStatus.Confirmed;

                        this.setState({
                            assignment,
                            jobDetailsOpen: !confirmed,
                            loading: false,
                            enforceCrewLeadTimesheet: enforceCrewLeadTS,
                            arcGisLocation,
                        });
                    }
                } else if (
                    response.status === util.fetch.statusCodes.unauthorized
                ) {
                    util.navigation.localRedirect(
                        this,
                        AppNavPaths.Unauthorized
                    );
                } else {
                    toasty.error(
                        'There was a problem retrieving the job assignment.'
                    );
                }
            }
        }
    };

    submitTimesheet = async () => {
        await this.setState({ timesheetValidated: false });
        await this.validateTimesheet();
        if (this.state.timesheetValid) {
            // TODO: save
        }
    };

    validateTimesheet = async () => {
        const { assignment } = { ...this.state };
        const timeFormat = 'HH:mm a';
        const invalidChargeTypeIds = [];
        for (const a of assignment.timesheet.assignments) {
            const valid =
                !!a.notApplicable ||
                // Start
                (!!a.startTime &&
                    !!moment(a.startTime, timeFormat, true).isValid()) ||
                // end
                !!a.endTime ||
                !!moment(a.endTime, timeFormat, true).isValid();
            if (!valid) invalidChargeTypeIds.push(a.id);
        }
        this.setState({
            timesheetValidated: true,
            timesheetValid:
                !invalidChargeTypeIds.length &&
                !!assignment.timesheet.workOrderNumber,
            invalidChargeTypeIds,
        });
    };

    // Helper method to get the earliest JRO start time for the employee and their equipment
    // JRO start times are UTC and are converted to local time for comparison
    getEarliestJROStartTime = () => {
        const { assignment } = this.state;
        if (!assignment) {
            return null;
        }

        const localStartTimes = [];

        // Add the employee's own JRO start time if it exists (convert from UTC to local)
        if (assignment.jobResourceOverride?.startTime) {
            const localMoment = fromUtcToLocalMoment(
                assignment.jobResourceOverride.startTime
            );
            localStartTimes.push(localMoment);
        }

        // Add start times from equipment owned by the main employee
        const ownedEquipment = (assignment.equipment ?? []).filter(
            (x) =>
                x.equipmentEmployeeAssignmentId ===
                assignment.assignedEmployeeId
        );

        ownedEquipment.forEach((eq) => {
            if (eq.jobResourceOverride?.startTime) {
                const localMoment = fromUtcToLocalMoment(
                    eq.jobResourceOverride.startTime
                );
                localStartTimes.push(localMoment);
            }
        });

        // If we have any JRO start times, return the earliest one as a Date
        if (localStartTimes.length > 0) {
            const earliestMoment = localStartTimes.reduce(
                (earliest, current) => {
                    return current.isBefore(earliest) ? current : earliest;
                }
            );
            return earliestMoment.toDate();
        }

        return null;
    };

    // Helper method to create a synthetic override object for display purposes
    // This is used when a crew/equipment member doesn't have an override but the main employee does
    createDefaultOverrideDisplay = (defaultStartTime) => {
        return {
            startTime: defaultStartTime,
            isDefault: true,
            isLocalTime: true,
        };
    };

    // Helper method to get equipment JRO if different from main employee's JRO
    getEquipmentJROIfDifferent = (equipment) => {
        const { assignment } = this.state;
        if (!assignment) {
            return null;
        }

        // Get the earliest JRO start time for the employee
        const earliestEmployeeJRO = this.getEarliestJROStartTime();

        // If main employee has a JRO, compare equipment's JRO against it
        if (earliestEmployeeJRO) {
            if (equipment.jobResourceOverride) {
                // Equipment has an override - check if it's different
                const equipJROLocalMoment = fromUtcToLocalMoment(
                    equipment.jobResourceOverride.startTime
                );
                const equipJROTime = equipJROLocalMoment.toDate();
                if (equipJROTime.getTime() !== earliestEmployeeJRO.getTime()) {
                    return equipment.jobResourceOverride;
                }
            } else {
                // Equipment has no override but main employee does - show default job time from equipment's local time
                if (equipment.startTime) {
                    return this.createDefaultOverrideDisplay(
                        equipment.startTime
                    );
                }
            }
        } else if (equipment.jobResourceOverride) {
            // No main employee JRO, but equipment has one
            return equipment.jobResourceOverride;
        }

        return null;
    };

    // Helper method to get crew member JRO if different from main employee's JRO
    getCrewJROIfDifferent = (crewMember) => {
        const { assignment } = this.state;
        if (
            !assignment ||
            crewMember.assignedEmployeeId === assignment.assignedEmployeeId
        ) {
            return null;
        }

        // Get the earliest JRO start time for the main employee
        const earliestMainJRO = this.getEarliestJROStartTime();

        // If main employee has a JRO, compare crew member's JRO against it
        if (earliestMainJRO) {
            if (crewMember.jobResourceOverride) {
                // Crew member has an override - check if it's different
                const crewJROLocalMoment = fromUtcToLocalMoment(
                    crewMember.jobResourceOverride.startTime
                );
                const crewJROTime = crewJROLocalMoment.toDate();
                if (crewJROTime.getTime() !== earliestMainJRO.getTime()) {
                    return crewMember.jobResourceOverride;
                }
            } else {
                // Crew member has no override but main employee does - show default job time from crew member's local time
                if (crewMember.startTime) {
                    return this.createDefaultOverrideDisplay(
                        crewMember.startTime
                    );
                }
            }
        } else if (crewMember.jobResourceOverride) {
            // No main employee JRO, but crew member has one
            return crewMember.jobResourceOverride;
        }

        return null;
    };

    render() {
        const {
            assignment,
            confirmedAssignments,
            jobDetailsOpen,
            loading,
            saving,
            notFound,
            strings,
        } = this.state;

        const { tenantSettings } = { ...this.context.tenant };

        const jobDetailsHeader =
            tenantSettings &&
            tenantSettings.showContractDescriptionInTimesheetView &&
            assignment &&
            assignment.jobName
                ? `Job Details - ${assignment.jobName}`
                : 'Job Details';

        const confirmed =
            (assignment ?? {}).confirmationStatusId ===
            ConfirmationStatus.Confirmed;
        const crewLead = strings ? strings[l.crewLead] : '';

        if (!!loading || !(this.context.tenant ?? {}).tenantSettings) {
            return (
                <div className="confirmationContainer h-100 pt-2 d-flex flex-row align-items-center justify-content-center">
                    <CircularProgress
                        color="secondary"
                        variant="indeterminate"
                    />
                </div>
            );
        }
        const { jobUploadsEnabled } = this.context.tenant.tenantSettings;

        const allEquipment = assignment.equipment ?? [];

        // What equipment does the confirming employee own of this list?
        const ownedEquipment = allEquipment.filter(
            (x) =>
                x.equipmentEmployeeAssignmentId ===
                assignment.assignedEmployeeId
        );
        const ownedEquipmentIds = ownedEquipment.map(
            (x) => x.assignedEquipmentId
        );

        // Of the equipment, which have been assigned to any of the equipment owned by the confirming employee?
        const dependentEquipment = allEquipment.filter((x) =>
            ownedEquipmentIds.includes(x.equipmentAssignedEquipmentId)
        );
        const dependentEquipmentIds = dependentEquipment.map(
            (x) => x.assignedEquipmentId
        );

        return (
            <>
                <div className="confirmationContainer h-100 pt-2">
                    <Container size="sm">
                        {!!notFound && (
                            <h4 className="text-center">
                                Unable to locate the requested resource.
                            </h4>
                        )}
                        {!!assignment && (
                            <>
                                {!!confirmed && (
                                    <Alert
                                        className="mb-3 p-1 text-center"
                                        color="success"
                                    >
                                        <FontAwesomeIcon
                                            icon={faCheckCircle}
                                            size="lg"
                                            className="mr-2"
                                        />
                                        This assignment has been confirmed.
                                    </Alert>
                                )}

                                <CollapseUnderlineHeader
                                    headerText={jobDetailsHeader}
                                    isOpen // jobDetailsOpen}
                                    toggleCollapse={() =>
                                        this.setState({
                                            jobDetailsOpen: !jobDetailsOpen,
                                        })
                                    }
                                >
                                    {(assignment ?? {}).confirmationStatusId ===
                                        ConfirmationStatus.CancelPending && (
                                        <Alert
                                            className="mb-3 p-1 text-center"
                                            color="danger"
                                        >
                                            <FontAwesomeIcon
                                                icon={faTimesCircle}
                                                size="lg"
                                                className="mr-2"
                                            />
                                            This assignment has been cancelled.
                                        </Alert>
                                    )}
                                    {(assignment ?? {}).confirmationStatusId ===
                                        ConfirmationStatus.ThresholdCancel && (
                                        <Alert
                                            className="mb-3 p-1 text-center"
                                            color="danger"
                                        >
                                            <FontAwesomeIcon
                                                icon={faTimesCircle}
                                                size="lg"
                                                className="mr-2"
                                            />
                                            This assignment has been cancelled
                                            by the customer.
                                        </Alert>
                                    )}
                                    <JobDetails
                                        {...assignment}
                                        tenantSettings={tenantSettings}
                                        jroStartTime={this.getEarliestJROStartTime()}
                                    />

                                    <AssignmentLocationDisplay
                                        {...this.state}
                                        jobUploadsEnabled={jobUploadsEnabled}
                                        makeLink={true}
                                    />

                                    {(assignment ?? {}).confirmationStatusId !==
                                        5 && (
                                        <div className="crewAndEquipmentDetails">
                                            <FormGroup>
                                                <FormLabel
                                                    text="Crew And Equipment"
                                                    className="bottom-border"
                                                />
                                            </FormGroup>
                                            {!!confirmed &&
                                                !!assignment.crewSMS && (
                                                    <FlexCenterRow>
                                                        <a
                                                            className="mb-2 p-1 btn btn-success btn-sm groupSmsLink text-white"
                                                            href={`sms:${assignment.crewSMS}`}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={
                                                                    faComments
                                                                }
                                                                size="lg"
                                                                className="mr-2"
                                                            />
                                                            Send Group SMS to
                                                            Crew
                                                        </a>
                                                    </FlexCenterRow>
                                                )}

                                            <div className="crewAndEquipmentDetails table-responsive">
                                                <table className="table table-sm assignmentsTable">
                                                    <tbody>
                                                        {!!assignment.crew && (
                                                            <>
                                                                {assignment.crew.map(
                                                                    (c) => {
                                                                        const ownedAssignment =
                                                                            c.assignedEmployeeId ===
                                                                            assignment.assignedEmployeeId;

                                                                        return (
                                                                            <tr
                                                                                className={cls(
                                                                                    'assignmentRow',
                                                                                    {
                                                                                        owned: !!ownedAssignment,
                                                                                    }
                                                                                )}
                                                                                key={
                                                                                    c.id
                                                                                }
                                                                            >
                                                                                <td className="iconCell">
                                                                                    <FontAwesomeIcon
                                                                                        className={cls(
                                                                                            {
                                                                                                'text-success font-weight-bold':
                                                                                                    !!ownedAssignment,
                                                                                                'text-muted':
                                                                                                    !ownedAssignment,
                                                                                                'fa-hard-hat-golden':
                                                                                                    !!c.isCrewLead,
                                                                                            }
                                                                                        )}
                                                                                        icon={
                                                                                            c.isCrewLead
                                                                                                ? faHardHat
                                                                                                : faUser
                                                                                        }
                                                                                        title={
                                                                                            c.isCrewLead
                                                                                                ? crewLead
                                                                                                : c.assignedEmployeeGroup
                                                                                        }
                                                                                    />
                                                                                </td>
                                                                                <td>
                                                                                    <FlexBetweenRow>
                                                                                        <small
                                                                                            className={cls(
                                                                                                {
                                                                                                    'text-success font-weight-bold':
                                                                                                        c.assignedEmployeeId ===
                                                                                                        assignment.assignedEmployeeId,
                                                                                                }
                                                                                            )}
                                                                                        >
                                                                                            {`${
                                                                                                c.assignedEmployeeName
                                                                                            } ${
                                                                                                c.isCrewLead
                                                                                                    ? `(${crewLead})`
                                                                                                    : ''
                                                                                            }`}
                                                                                        </small>
                                                                                        {!!confirmed &&
                                                                                            !!c.assignedEmployeePhone && (
                                                                                                <a
                                                                                                    title={`Send SMS to ${c.assignedEmployeeName}`}
                                                                                                    className="ml-2 p-1 btn btn-sm btn-success"
                                                                                                    href={`sms://+1${c.assignedEmployeePhone}`}
                                                                                                >
                                                                                                    <FontAwesomeIcon
                                                                                                        icon={
                                                                                                            faCommentDots
                                                                                                        }
                                                                                                        size="lg"
                                                                                                    />
                                                                                                </a>
                                                                                            )}
                                                                                    </FlexBetweenRow>
                                                                                    {this.getCrewJROIfDifferent(
                                                                                        c
                                                                                    ) && (
                                                                                        <JobResourceOverrideDetails
                                                                                            override={this.getCrewJROIfDifferent(
                                                                                                c
                                                                                            )}
                                                                                        />
                                                                                    )}
                                                                                </td>
                                                                                <td className="text-right">
                                                                                    {!confirmed &&
                                                                                        c.confirmationStatusId ===
                                                                                            ConfirmationStatus.Confirmed && (
                                                                                            <FontAwesomeIcon
                                                                                                style={{
                                                                                                    opacity:
                                                                                                        ownedAssignment
                                                                                                            ? 1
                                                                                                            : 0.5,
                                                                                                }}
                                                                                                size="lg"
                                                                                                title="Employee confirmed"
                                                                                                className="icon-check checked"
                                                                                                icon={
                                                                                                    faCheckCircle
                                                                                                }
                                                                                            />
                                                                                        )}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    }
                                                                )}
                                                            </>
                                                        )}
                                                        {!!assignment.equipment && (
                                                            <>
                                                                {assignment.equipment.map(
                                                                    (e) => {
                                                                        const icon =
                                                                            e.assignedEquipmentType
                                                                                .toLowerCase()
                                                                                .includes(
                                                                                    'truck'
                                                                                )
                                                                                ? faTruck
                                                                                : e.assignedEquipmentType
                                                                                        .toLowerCase()
                                                                                        .includes(
                                                                                            'van'
                                                                                        )
                                                                                  ? faShuttleVan
                                                                                  : faToolbox;

                                                                        const ownedAssignment =
                                                                            e.equipmentEmployeeAssignmentId ===
                                                                                assignment.assignedEmployeeId ||
                                                                            dependentEquipmentIds.includes(
                                                                                e.assignedEquipmentId
                                                                            );

                                                                        const clickable =
                                                                            !confirmed &&
                                                                            e.confirmationStatusId !==
                                                                                ConfirmationStatus.Confirmed &&
                                                                            ownedAssignment;

                                                                        return (
                                                                            <tr
                                                                                className={cls(
                                                                                    'assignmentRow',
                                                                                    {
                                                                                        clickable,
                                                                                        owned: !!ownedAssignment,
                                                                                    }
                                                                                )}
                                                                                key={
                                                                                    e.id
                                                                                }
                                                                                role={
                                                                                    clickable
                                                                                        ? 'button'
                                                                                        : 'row'
                                                                                }
                                                                                onClick={() => {
                                                                                    if (
                                                                                        clickable
                                                                                    ) {
                                                                                        this.onAssignmentCheckedChanged(
                                                                                            e.id
                                                                                        );
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <td className="iconCell">
                                                                                    <FontAwesomeIcon
                                                                                        className={cls(
                                                                                            {
                                                                                                'text-success font-weight-bold':
                                                                                                    !!ownedAssignment,
                                                                                                'text-muted':
                                                                                                    !ownedAssignment,
                                                                                            }
                                                                                        )}
                                                                                        icon={
                                                                                            icon
                                                                                        }
                                                                                    />
                                                                                </td>
                                                                                <td>
                                                                                    <FlexColumnStart>
                                                                                        <small
                                                                                            className={cls(
                                                                                                {
                                                                                                    'text-success font-weight-bold':
                                                                                                        !!ownedAssignment,
                                                                                                }
                                                                                            )}
                                                                                        >
                                                                                            {`${e.assignedEquipmentType} ${e.assignedEquipmentName}`}
                                                                                        </small>
                                                                                        {e.equipmentEmployeeAssignmentName && (
                                                                                            <small>{`(Assigned to: ${e.equipmentEmployeeAssignmentName})`}</small>
                                                                                        )}
                                                                                        {e.equipmentAssignedEquipmentName && (
                                                                                            <small>{`(Assigned to: ${e.equipmentAssignedEquipmentType} ${e.equipmentAssignedEquipmentName})`}</small>
                                                                                        )}
                                                                                        {this.getEquipmentJROIfDifferent(
                                                                                            e
                                                                                        ) && (
                                                                                            <JobResourceOverrideDetails
                                                                                                override={this.getEquipmentJROIfDifferent(
                                                                                                    e
                                                                                                )}
                                                                                            />
                                                                                        )}
                                                                                    </FlexColumnStart>
                                                                                </td>
                                                                                <td className="text-right">
                                                                                    {!confirmed &&
                                                                                        e.confirmationStatusId ===
                                                                                            ConfirmationStatus.Confirmed && (
                                                                                            <FontAwesomeIcon
                                                                                                style={{
                                                                                                    opacity:
                                                                                                        ownedAssignment
                                                                                                            ? 1
                                                                                                            : 0.7,
                                                                                                }}
                                                                                                size="lg"
                                                                                                title="Equipment confirmed"
                                                                                                className="icon-check checked"
                                                                                                icon={
                                                                                                    faCheckCircle
                                                                                                }
                                                                                            />
                                                                                        )}
                                                                                    {!confirmed &&
                                                                                        e.confirmationStatusId !==
                                                                                            ConfirmationStatus.Confirmed &&
                                                                                        ownedAssignment && (
                                                                                            <FontAwesomeIcon
                                                                                                style={{
                                                                                                    opacity:
                                                                                                        ownedAssignment
                                                                                                            ? 1
                                                                                                            : 0.7,
                                                                                                }}
                                                                                                size="lg"
                                                                                                className={cls(
                                                                                                    'cursor-pointer icon-check',
                                                                                                    {
                                                                                                        checked:
                                                                                                            confirmedAssignments.indexOf(
                                                                                                                e.id
                                                                                                            ) >
                                                                                                            -1,
                                                                                                    }
                                                                                                )}
                                                                                                title={
                                                                                                    confirmedAssignments.indexOf(
                                                                                                        e.id
                                                                                                    ) >
                                                                                                    -1
                                                                                                        ? 'Confirmed'
                                                                                                        : 'Click to confirm'
                                                                                                }
                                                                                                icon={
                                                                                                    faCheckCircle
                                                                                                }
                                                                                            />
                                                                                        )}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    }
                                                                )}
                                                            </>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </CollapseUnderlineHeader>
                            </>
                        )}
                    </Container>
                </div>
                <FlexCenterRow className="userActions p-1">
                    {this.state?.assignment?.dispatchOfficeNumber && (
                        <Button
                            type="button"
                            id="officeCallButton"
                            href={`tel:${this.state?.assignment?.dispatchOfficeNumber}`}
                        >
                            <FontAwesomeIcon
                                size="lg"
                                icon={faPhoneSquareAlt}
                                className="mr-2"
                            />
                            {`Office - ${this.state?.assignment?.dispatchOfficeNumber}`}
                        </Button>
                    )}
                    {(assignment ?? {}).confirmationStatusId <= 3 && (
                        <Button
                            type="button"
                            color="primary"
                            disabled={saving}
                            onClick={() => this.confirmAssignment()}
                        >
                            <FontAwesomeIcon
                                size="lg"
                                icon={saving ? faCircleNotch : faCheckCircle}
                                className="mr-2"
                            />
                            Confirm
                        </Button>
                    )}
                    {(assignment ?? {}).confirmationStatusId === 5 && (
                        <Button
                            type="button"
                            color="danger"
                            disabled={saving}
                            onClick={() => this.confirmCancelledAssignment()}
                        >
                            <FontAwesomeIcon
                                size="lg"
                                icon={saving ? faCircleNotch : faCheckCircle}
                                className="mr-2"
                            />
                            Confirm Cancellation
                        </Button>
                    )}
                    {assignment.isCrewLead && !!assignment?.timesheetId && (
                        <Button
                            type="button"
                            color="primary"
                            onClick={() =>
                                this.props.history.push(
                                    `${AppNavPaths.CrewTimesheet}/${assignment.timesheetId}`
                                )
                            }
                        >
                            <FontAwesomeIcon size="lg" icon={faUserClock} />
                            Go To Timesheet
                        </Button>
                    )}
                </FlexCenterRow>
            </>
        );
    }
}

export default withRouter(AssignmentConfirmation);
